const EMPTY_ERROR_MESSAGE = 'Unknown error';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const formatGenerationError = (error: unknown): string => {
  if (typeof error === 'string') {
    return error.trim() || EMPTY_ERROR_MESSAGE;
  }

  if (typeof error === 'number' || typeof error === 'boolean') {
    return String(error);
  }

  if (error instanceof Error) {
    return error.message?.trim() || EMPTY_ERROR_MESSAGE;
  }

  if (Array.isArray(error)) {
    const parsed = error.map(item => formatGenerationError(item)).filter(Boolean);
    return parsed.length > 0 ? parsed.join(', ') : EMPTY_ERROR_MESSAGE;
  }

  if (isRecord(error)) {
    const preferredKeys = [
      'message',
      'error',
      'reason',
      'detail',
      'details',
      'description',
    ];

    for (const key of preferredKeys) {
      const value = error[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
      if (value != null && typeof value === 'object') {
        const nested = formatGenerationError(value);
        if (nested && nested !== EMPTY_ERROR_MESSAGE) {
          return nested;
        }
      }
    }

    if (Array.isArray(error.errors)) {
      const nestedErrors = error.errors
        .map(item => formatGenerationError(item))
        .filter(Boolean);
      if (nestedErrors.length > 0) {
        return nestedErrors.join(', ');
      }
    }

    try {
      const serialized = JSON.stringify(error);
      return serialized && serialized !== '{}' ? serialized : EMPTY_ERROR_MESSAGE;
    } catch {
      return EMPTY_ERROR_MESSAGE;
    }
  }

  return EMPTY_ERROR_MESSAGE;
};

export const formatGenerationErrors = (errors: unknown): string[] => {
  if (!Array.isArray(errors)) return [];

  return errors
    .map(item => formatGenerationError(item))
    .filter(message => Boolean(message && message.trim()));
};
